import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Flex,
  DeprecatedIcon,
  PageSpinner,
  SegmentedControl,
  Spinner,
  Text,
} from '@fullstory/components';
import { AppDispatch, MatchingUsersActions } from '@fullstory/redux';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { EventSearch } from '@fullstory/proto/fs/api/search';
import { bestDisplayNameforUser } from '@fullstory/util/utils';
import { Capture } from '@fullstory/tomato-components';
import { LocationMap } from '../LocationMap';
import { SessionList } from '../SessionList';
import { AvatarImage } from '../Avatar';
import { classNames } from '@fullstory/util/fmt';
import css from './userOverlay.module.scss';
import { FormattedMessage } from 'react-intl';
import { useUserOverlay } from './hooks/useUserOverlay';
import { UserOverlayDetails } from './userOverlayDetails';
import { UserOverlayDeleteUserBtn } from './userOverlayDeleteUserBtn';
import { UserOverlayExports } from './userOverlayExport';
import { useFlags } from '@fullstory/react-hooks';
import { Flag } from '@fullstory/proto/fs/shared/flags';

const overlayContainer = 'userDetailContainer';

export const UserOverlay: React.FC<{
  userId: string;
  orgId: string;
  segmentDefinition: SegmentDefinition;
  eventSearch?: EventSearch;
}> = ({ userId, orgId, segmentDefinition, eventSearch }) => {
  // TODO (timchin): once the overlays are all converted to React, remove this
  Capture.ignoreClicksOnClasses[overlayContainer] = true;
  const dispatch = useDispatch<AppDispatch>();
  const {
    userDetails,
    userMetadata,
    sessionFilter,
    setSessionFilter,
    sessions,
    clips,
    segmentedOptions,
    loading,
    error,
    gravatarUrl,
  } = useUserOverlay(userId, orgId, segmentDefinition, eventSearch);

  const flags = useFlags();

  return (
    <div data-component="UserOverlay" className={classNames(overlayContainer, 'fs-mask')}>
      {error ? (
        <Box padding={24}>
          <Flex justifyContent="center" alignItems="center" column>
            <Box paddingBottom={16}>
              <DeprecatedIcon name="error-warning-fill" size={16} color="red-bright" />
            </Box>
            <Text appearance="bodySmall">
              <FormattedMessage
                defaultMessage="Failed to load user. If the problem persists, drop us a note using the Support button."
                id="f5PWJe"
              />
            </Text>
          </Flex>
        </Box>
      ) : loading && !userDetails ? (
        <Flex justifyContent="center" alignItems="center">
          <Box marginTop={24}>
            <Spinner />
          </Box>
        </Flex>
      ) : null}
      {!error && userDetails != null ? (
        <Box>
          <div className={css.headerContainer}>
            <LocationMap latLong={userDetails?.UserLastLatLong} />
            <div className={css.avatar}>
              <AvatarImage
                indivId={userId}
                email={userDetails.UserEmail}
                displayName={userDetails.UserDisplayName}
                gravatarURL={gravatarUrl}
                size={80}
              />
            </div>
            <Box className={css.titleBar}>
              <Box marginLeft={48} paddingTop={16} paddingBottom={16} marginBottom={12}>
                <Text appearance="heading2" color="default-inverse" tag="div">
                  {bestDisplayNameforUser(userDetails.UserDisplayName, userDetails.UserEmail)}
                </Text>
                <Text appearance="bodyXSmallBold" color="default-inverse">
                  <FormattedMessage
                    defaultMessage="{count, plural, one {# session} other {# sessions}} • Since {timestamp, date, ::MMMM y}"
                    id="kO/Zoj"
                    values={{
                      count: userDetails.TotalSessionCount,
                      timestamp: userDetails.UserCreationTime,
                    }}
                  />
                </Text>
              </Box>
            </Box>
          </div>
          {!loading && sessionFilter ? (
            <>
              <Box padding={[16, 24, 16, 24]}>
                <SegmentedControl
                  size="small"
                  options={segmentedOptions}
                  selected={sessionFilter}
                  onChange={setSessionFilter}
                />
              </Box>
              <SessionList
                sessions={sessions}
                clips={clips}
                onPlayClick={() =>
                  // TODO (timchin): make autoplay to work with user overlay
                  dispatch(MatchingUsersActions.resetAutoplay())
                }
              />
            </>
          ) : (
            <PageSpinner diameter={20} />
          )}
          <Box marginTop={24} marginLeft={24}>
            <Text appearance="bodyLargeBold" color="default">
              <FormattedMessage defaultMessage="User Properties" id="uNKKHK" />
            </Text>
          </Box>
          <UserOverlayDetails userDetails={userDetails} userMetadata={userMetadata} />
          <Box padding={24}>
            <UserOverlayExports
              userDetails={userDetails}
              disabled={!flags?.hasBit(Flag.PermExportData)}
            />
            <UserOverlayDeleteUserBtn userDetails={userDetails} userId={userId} />
          </Box>
        </Box>
      ) : null}
    </div>
  );
};
